// PAINT-MODAL CONTROLLER — the cross-tree opener for the ONE house
// PaintPickerModal (owner directive, 2026-07-21: "a standard color selector
// modal ... that opens in the middle of the screen regardless of where color
// is selected").
//
// A module-level registered setter — the SAME pub/sub shape StylesExplorerModal
// uses for its Esc stack — so ANY color entry point (a Row-2 chip, the Poster
// flyout background chip, a drawer / effect-param PaintField) opens the
// singleton without prop-drilling or a shared React context. This module holds
// NO React and imports nothing (breaks the PaintField → modal → PaintPicker
// cycle at the seam): the host component registers its state setter on mount;
// openPaintModal pushes a config to it.
//
// ONE modal at a time: opening for a new target REPLACES the open one (the
// "one floating surface" law). The bumped _id is used as the modal's React key
// so a replace remounts it — re-centering + re-snapshotting for the new target.
let _setConfig = null;
let _seq = 0;

/** The singleton host (PaintPickerModalHost) registers its setState here on
 *  mount and returns an unregister fn for unmount. */
export function registerPaintModalHost(setter) {
  _setConfig = setter;
  return () => { if (_setConfig === setter) _setConfig = null; };
}

/** Open the house paint modal for ONE color target. `config` carries the
 *  PaintPicker prop grammar verbatim — value, onChange(next, {live}), onCommit,
 *  allow, density, recentKey, palette, linearOnly, opaque, allowImport,
 *  onBrowseAll — plus { title, fieldId } for the modal chrome + probe hook. */
export function openPaintModal(config) {
  if (typeof _setConfig !== 'function') {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[paintModal] openPaintModal called with no PaintPickerModalHost mounted');
    }
    return;
  }
  _setConfig({ ...config, _id: ++_seq });
}

/** Close the modal if open. No revert — callers that need the revert-to-open
 *  behavior use the modal's own ✕/Esc path (see PaintPickerModal). */
export function closePaintModal() {
  if (typeof _setConfig === 'function') _setConfig(null);
}

// ---- poster palette source (color library, 2026-07-21) -------------------
// The LibraryShelf's "From this poster" row needs the live fabric canvas.
// Same registered-getter shape as the modal host: RibbonMount registers once
// (it owns the editor facade); the shelf pulls through PaintPicker. Still no
// React, still imports nothing.
let _paletteCanvasGetter = null;

export function registerPaletteCanvasGetter(fn) {
  _paletteCanvasGetter = fn;
  return () => { if (_paletteCanvasGetter === fn) _paletteCanvasGetter = null; };
}

export function getRegisteredPaletteCanvas() {
  try { return typeof _paletteCanvasGetter === 'function' ? _paletteCanvasGetter() : null; } catch { return null; }
}
